/* eslint-disable max-lines-per-function */
import { filter, first, get, identity, map, uniq, uniqBy } from "lodash";
import React from "react";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { Button } from "components/haven-design";
import RemoveUserModal from "pages/dashboard/clients/interests/list/remove-user-modal";
import { PossibleWaitlistUpdateRemovalReasons } from "components/structured-reasons";
import { Interest } from "../../types";
import { FormProps } from "../types";
import { interestIsRemoved } from "../utils";
import AllRemovedBanner from "./all-removed";

const fullName = (interest: Interest | undefined): React.ReactNode =>
  (interest ? (
    [interest.householdMember0FirstName, interest.householdMember0LastName]
      .filter(identity)
      .join(" ")) : null);

const humanWaitTime = (waitTime?: string): string => {
  switch (waitTime) {
    case "HALF_YEAR_WAITLIST":
      return "Under 6 months";
    case "UNDER_YEAR_WAITLIST":
      return "Under 1 year";
    case "UNDER_TWO_YEARS_WAITLIST":
      return "1-2 years";
    case "UNDER_THREE_YEARS_WAITLIST":
      return "2-3 years";
    case "UNDER_FIVE_YEARS_WAITLIST":
      return "4-5 years";
    case "OVER_FIVE_YEARS_WAITLIST":
      return "5+ years";
    case "OVER_DECADE_WAITLIST":
      return "10+ years";
    default:
      return "Multi-year wait";
  }
};

const listActionColumnSx = {
  flex: "0 0 auto" as const,
  minWidth: "5.5rem",
  textAlign: "center" as const,
  py: 0.75,
  px: 1,
} as const;

type activeListItem = {
  id: string,
  name: string | null,
  city: string,
  state: string,
  status: string | undefined,
  waitTime?: string,
}

const ChooseUpdateForm: React.FC<FormProps> = (props) => {
  const { code } = useParams<{ code: string }>();
  const emails = uniq(filter(map(map(props.data.interests, "interestList"), "email")));
  const getActiveLists = () => uniqBy(
    props.data.interests
      .map((i) => ({
        id: i.id ?? "",
        name: i.interestList.name,
        city: get(i, "interestList.address.city", "") as string,
        state: get(i, "interestList.address.state", "") as string,
        status: i.status.name,
        waitTime: i.interestList.waitTime,
      })),
    (x) => x.name,
  );

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [interestListName, setInterestListName] = React.useState("");
  const [interestId, setInterestId] = React.useState("");
  const [activeLists] = React.useState(() => getActiveLists());


  const handleOpenRemoveDialog = (list: activeListItem) => {
    setInterestListName(list.name ?? "");
    setInterestId(list.id);
    setIsModalOpen(true);
  };

  const handleCloseRemoveDialog = (isRemoved: boolean) => {
    setIsModalOpen(false);

    if (isRemoved) {
      props.setRemoved(interestId);
    }
  };

  return (
    <div>
      <h3>Hi {fullName(first(props.data.interests))}.
      Here are the current lists you&apos;re on:</h3>

      <Stack spacing={0} sx={{ pt: 3.75, width: 1 }}>
        {activeLists.map((list) => {
          const removed = interestIsRemoved(list.id, list.status, props.removedInterests);
          return (
            <React.Fragment key={list.id || list.name}>
              <Divider />
              <Stack spacing={1.5} sx={{ px: 1, py: 1 }}>
                <b>
                  {list.name}
                  {" "}
                  {list.city && list.state && `(${list.city}, ${list.state})`}
                </b>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  justifyContent="space-between"
                  spacing={1}
                  useFlexGap
                  sx={{ width: 1 }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {humanWaitTime(list.waitTime)} estimated wait time
                  </Box>
                  {removed ? (
                    <Box
                      sx={{ ...listActionColumnSx, color: "error.main" }}
                    >
                      <b>Removed</b>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        ...listActionColumnSx,
                        "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } },
                      }}
                    >
                      <Button
                        color="secondary"
                        onClick={() => handleOpenRemoveDialog(list)}
                      >
                        Remove
                      </Button>
                    </Box>
                  )}
                </Stack>
              </Stack>
            </React.Fragment>
          );
        })}
        {activeLists.length > 0 && <Divider />}
        {activeLists.every(
          (list) => interestIsRemoved(list.id, list.status, props.removedInterests),
        ) && (
          <Box sx={{ pt: 4 }}>
            <AllRemovedBanner />
          </Box>
        )}
      </Stack>

      <RemoveUserModal
        isOpen={isModalOpen}
        onRequestClose={handleCloseRemoveDialog}
        interestListName={interestListName || ""}
        interestId={interestId}
        removalReasons={PossibleWaitlistUpdateRemovalReasons}
        waitlistCode={code}
      />

      {emails.length > 0 && (
        <Typography
          component="p"
          sx={{ mt: 3 }}
        >
          If your name is misspelled, please contact the property manager(s) at{" "}
          {emails
            .map<React.ReactNode>((email) => <a key={email} href={`mailto:${email}`}>{email}</a>)
            .reduce((result, element) => [result, ", ", element])}
        </Typography>
      )}
    </div>
  );
};

export default ChooseUpdateForm;
